import * as React from 'react';
import { cva, type VariantProps } from 'class-variance-authority';
import { CheckIcon, XCircle, ChevronDown, XIcon, WandSparkles } from 'lucide-react';

import { cn } from '@/lib/utils';
import { Separator } from '@/components/ui/separator';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
  CommandSeparator,
} from '@/components/ui/command';
import { useTranslation } from 'react-i18next';

const multiSelectVariants = cva(
  'tw-m-1 tw-transition tw-ease-in-out tw-delay-150 hover:tw--translate-y-1 hover:tw-scale-110 tw-duration-300',
  {
    variants: {
      variant: {
        default: 'tw-border-foreground/10 tw-text-foreground tw-bg-card hover:tw-bg-card/80',
        secondary:
          'tw-border-foreground/10 tw-bg-secondary tw-text-secondary-foreground hover:tw-bg-secondary/80',
        destructive:
          'tw-border-transparent tw-bg-destructive tw-text-destructive-foreground hover:tw-bg-destructive/80',
        inverted: 'inverted',
      },
    },
    defaultVariants: {
      variant: 'default',
    },
  },
);

interface MultiSelectProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement>,
    VariantProps<typeof multiSelectVariants> {
  options: {
    label: string;
    value: string;
    icon?: React.ComponentType<{ className?: string }>;
  }[];
  onValueChange: (value: string[]) => void;
  defaultValue?: string[];
  placeholder?: string;
  animation?: number;
  maxCount?: number;
  modalPopover?: boolean;
  asChild?: boolean;
  className?: string;
}

export const MultiSelect = React.forwardRef<HTMLButtonElement, MultiSelectProps>(
  (
    {
      options,
      onValueChange,
      variant,
      defaultValue = [],
      placeholder = '',
      animation = 0,
      maxCount = 3,
      modalPopover = false,
      asChild = false,
      className,
      ...props
    },
    ref,
  ) => {
    const { t } = useTranslation('translation', { keyPrefix: 'inputs' });
    const [selectedValues, setSelectedValues] = React.useState<string[]>(defaultValue);
    const [isPopoverOpen, setIsPopoverOpen] = React.useState(false);
    const [isAnimating, setIsAnimating] = React.useState(false);

    const handleInputKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
      if (event.key === 'Enter') {
        setIsPopoverOpen(true);
      } else if (event.key === 'Backspace' && !event.currentTarget.value) {
        const newSelectedValues = [...selectedValues];
        newSelectedValues.pop();
        setSelectedValues(newSelectedValues);
        onValueChange(newSelectedValues);
      }
    };

    const toggleOption = (option: string) => {
      const newSelectedValues = selectedValues.includes(option)
        ? selectedValues.filter((value) => value !== option)
        : [...selectedValues, option];
      setSelectedValues(newSelectedValues);
      onValueChange(newSelectedValues);
    };

    const handleClear = () => {
      setSelectedValues([]);
      onValueChange([]);
    };

    const handleTogglePopover = () => setIsPopoverOpen((prev) => !prev);
    const clearExtraOptions = () => {
      const newSelectedValues = selectedValues.slice(0, maxCount);
      setSelectedValues(newSelectedValues);
      onValueChange(newSelectedValues);
    };

    const toggleAll = () => {
      if (selectedValues.length === options.length) {
        handleClear();
      } else {
        const allValues = options.map((option) => option.value);
        setSelectedValues(allValues);
        onValueChange(allValues);
      }
    };

    return (
      <Popover  open={isPopoverOpen} onOpenChange={setIsPopoverOpen} modal={modalPopover}>
        <PopoverTrigger asChild>
          <Button
            ref={ref}
            {...props}
            onClick={handleTogglePopover}
            className={cn(
              'tw-flex !tw-bg-transparent tw-w-full tw-p-1 !tw-px-3 tw-rounded-md tw-border tw-min-h-10 tw-h-auto tw-items-center tw-justify-between hover:tw-bg-inherit [&_svg]:tw-pointer-events-auto',
              className,
            )}
          >
            {selectedValues.length > 0 ? (
              <div className='tw-flex tw-justify-between tw-items-center tw-w-full'>
                <div className='tw-flex tw-flex-wrap tw-items-center'>
                  {selectedValues.slice(0, maxCount).map((value) => {
                    const option = options.find((o) => o.value === value);
                    const IconComponent = option?.icon;
                    return (
                      <Badge
                        key={value}
                        className={cn(
                          isAnimating ? 'tw-animate-bounce' : '',
                          multiSelectVariants({ variant }),
                        )}
                        style={{ animationDuration: `${animation}s` }}
                      >
                        {IconComponent && <IconComponent className='tw-h-4 tw-w-4 tw-mr-2' />}
                        {option?.label}
                        <XCircle
                          className='tw-ml-2 tw-h-4 tw-w-4 tw-cursor-pointer'
                          onClick={(e) => {
                            e.stopPropagation();
                            toggleOption(value);
                          }}
                        />
                      </Badge>
                    );
                  })}
                  {selectedValues.length > maxCount && (
                    <Badge
                      className={cn(
                        'tw-bg-transparent tw-text-foreground tw-border-foreground/1 hover:tw-bg-transparent',
                        isAnimating ? 'tw-animate-bounce' : '',
                        multiSelectVariants({ variant }),
                      )}
                      style={{ animationDuration: `${animation}s` }}
                    >
                      {`+ ${selectedValues.length - maxCount} ${t('more')}`}
                      <XCircle
                        className='tw-ml-2 tw-h-4 tw-w-4 tw-cursor-pointer'
                        onClick={(e) => {
                          e.stopPropagation();
                          clearExtraOptions();
                        }}
                      />
                    </Badge>
                  )}
                </div>
                <div className='tw-flex tw-items-center tw-justify-between'>
                  <XIcon
                    className='tw-h-4 tw-mx-2 tw-cursor-pointer tw-text-muted-foreground'
                    onClick={(e) => {
                      e.stopPropagation();
                      handleClear();
                    }}
                  />
                  <Separator orientation='vertical' className='tw-flex tw-min-h-6 tw-h-full' />
                  <ChevronDown className='tw-h-4 tw-mx-2 tw-cursor-pointer tw-text-muted-foreground' />
                </div>
              </div>
            ) : (
              <div className='tw-flex tw-items-center tw-justify-between tw-w-full tw-mx-auto'>
                <span className='tw-text-sm tw-text-muted-foreground tw-font-normal'>
                  {placeholder}
                </span>
                <ChevronDown className='tw-h-4 tw-cursor-pointer tw-text-muted-foreground' />
              </div>
            )}
          </Button>
        </PopoverTrigger>
        <PopoverContent
          className='tw-w-auto !tw-p-0'
          align='start'
          onEscapeKeyDown={() => setIsPopoverOpen(false)}
        >
          <Command>
            <CommandInput placeholder={t('search')} onKeyDown={handleInputKeyDown} />
            <CommandList>
              <CommandEmpty>{t('noResults')}</CommandEmpty>
              <CommandGroup>
                <CommandItem key='all' onSelect={toggleAll} className='tw-cursor-pointer'>
                  <div
                    className={cn(
                      'tw-mr-2 tw-flex tw-h-4 tw-w-4 tw-items-center tw-justify-center tw-rounded-sm tw-border tw-border-primary',
                      selectedValues.length === options.length
                        ? 'tw-bg-black tw-text-white'
                        : 'tw-opacity-50 [&_svg]:tw-invisible',
                    )}
                  >
                    <CheckIcon className='tw-h-4 tw-w-4' />
                  </div>
                  <span>{t('all')}</span>
                </CommandItem>
                {options.map((option) => {
                  const isSelected = selectedValues.includes(option.value);
                  return (
                    <CommandItem
                      key={option.value}
                      onSelect={() => toggleOption(option.value)}
                      className='tw-cursor-pointer'
                    >
                      <div
                        className={cn(
                          'tw-mr-2 tw-flex tw-h-4 tw-w-4 tw-items-center tw-justify-center tw-rounded-sm tw-border tw-border-primary',
                          isSelected
                            ? 'tw-bg-black tw-text-white'
                            : 'tw-opacity-50 [&_svg]:tw-invisible',
                        )}
                      >
                        <CheckIcon className='tw-h-4 tw-w-4' />
                      </div>
                      {option.icon && (
                        <option.icon className='tw-mr-2 tw-h-4 tw-w-4 tw-text-muted-foreground' />
                      )}
                      <span>{option.label}</span>
                    </CommandItem>
                  );
                })}
              </CommandGroup>
              <CommandSeparator />
              <CommandGroup>
                <div className='tw-flex tw-items-center tw-justify-between'>
                  {selectedValues.length > 0 && (
                    <>
                      <CommandItem
                        onSelect={handleClear}
                        className='tw-flex-1 tw-justify-center tw-cursor-pointer'
                      >
                        {t('clear')}
                      </CommandItem>
                      <Separator orientation='vertical' className='tw-flex tw-min-h-6 tw-h-full' />
                    </>
                  )}
                  <CommandItem
                    onSelect={() => setIsPopoverOpen(false)}
                    className='tw-flex-1 tw-justify-center tw-cursor-pointer tw-max-w-full'
                  >
                    {t('close')}
                  </CommandItem>
                </div>
              </CommandGroup>
            </CommandList>
          </Command>
        </PopoverContent>
        {animation > 0 && selectedValues.length > 0 && (
          <WandSparkles
            className={cn(
              'tw-cursor-pointer tw-my-2 tw-text-foreground tw-bg-background tw-w-3 tw-h-3',
              isAnimating ? '' : 'tw-text-muted-foreground',
            )}
            onClick={() => setIsAnimating(!isAnimating)}
          />
        )}
      </Popover>
    );
  },
);

MultiSelect.displayName = 'MultiSelect';
